import React from "react";
import PropTypes from "prop-types";
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";

import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Dialog from "@material-ui/core/Dialog";
import { BookmarkBorder } from "@material-ui/icons";

import Button from "components/CustomButtons/Button.js";
import ModalHeader from "components/ModalHeader";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CustomInput from "components/CustomInput/CustomInput";
import ActionDropdown from "components/ActionDropdown/ActionDropdown";
import ActionBarButton from "components/ActionBar/ActionBarButton";
import useThemeColors from "../../hooks/useThemeColors";

const stripText = (text) => {
  return text.replace(/^\s+|\s+$|\s+(?=\s)/g, "");
};

export default function ScenarioControls({
  scenarioHook,
  disabled,
  classes,
  notifyCopy,
}) {
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();

  const {
    busy,
    getDropdownOptions,
    handleSelection,
    nameModalOpen,
    setNameModalOpen,
    nameModalInput,
    handleNameChange,
    handleNameApply,
    nameInputRef,
    shareModalOpen,
    setShareModalOpen,
    shareURL,
    shareLoading,
    copyShareURL,
  } = scenarioHook;

  const handleCopy = () => {
    copyShareURL();
    if (notifyCopy) notifyCopy();
  };

  return (
    <>
      <ActionDropdown
        button={
          <ActionBarButton
            icon={<BookmarkBorder />}
            text={t("results.scenarios.scenarios")}
            disabled={disabled || busy}
          />
        }
        dropdownList={getDropdownOptions()}
        onClick={handleSelection}
      />

      {/* Name dialog (save / save as) */}
      <Dialog
        classes={{
          root: classes.modalRoot,
          paper: classes.modal,
        }}
        open={nameModalOpen}
        keepMounted
        onClose={() => setNameModalOpen(false)}
        aria-labelledby="scenario-name-modal-title"
        aria-describedby="scenario-name-modal-description"
        fullWidth
        maxWidth={"sm"}
      >
        <ModalHeader
          id="scenario-name-modal-title"
          title={t("results.scenarios.scenario_name")}
          onClose={() => setNameModalOpen(false)}
          closeDisabled={busy}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent
          id="scenario-name-modal-description"
          className={classes.modalBody}
        >
          <GridContainer alignItems={"center"}>
            <GridItem xs={12}>
              <CustomInput
                labelText={t("results.scenarios.set_a_name_for_the_scenario")}
                id="scenario_name"
                value={nameModalInput}
                onChange={handleNameChange}
                formControlProps={{ fullWidth: true }}
                inputProps={{
                  type: "text",
                  autoComplete: "off",
                  maxLength: "50",
                  disabled: busy,
                }}
                client
                helperText={
                  nameModalInput.length + "/50 " + t("results.characters")
                }
                ref={nameInputRef}
                onEnterKey={() => handleNameApply()}
              />
            </GridItem>
          </GridContainer>
        </DialogContent>
        <DialogActions className={classes.modalFooter}>
          <GridContainer>
            <GridItem>
              <GridContainer alignItems={"center"} style={{ height: "100%" }}>
                <GridItem>
                  <ThreeDots
                    visible={busy}
                    color={clientColor}
                    ariaLabel="loading"
                    height={"12px"}
                    wrapperStyle={{}}
                    wrapperClass=""
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
            <GridItem xs={true}></GridItem>
            <GridItem>
              <Button
                onClick={() => handleNameApply()}
                color="info"
                simple
                disabled={busy || stripText(nameModalInput) === ""}
              >
                {t("results.scenarios.save")}
              </Button>
              <Button
                onClick={() => setNameModalOpen(false)}
                color="danger"
                simple
                disabled={busy}
              >
                {t("results.filter_modal.close")}
              </Button>
            </GridItem>
          </GridContainer>
        </DialogActions>
      </Dialog>

      {/* Share dialog */}
      <Dialog
        classes={{
          root: classes.modalRoot,
          paper: classes.modal,
        }}
        open={shareModalOpen}
        keepMounted
        onClose={() => setShareModalOpen(false)}
        aria-labelledby="share-view-modal-title"
        aria-describedby="share-view-modal-description"
        fullWidth
        maxWidth={"sm"}
      >
        <ModalHeader
          id="share-view-modal-title"
          title={t("results.scenarios.share_view")}
          subtitle={t("results.scenarios.url_to_share")}
          onClose={() => setShareModalOpen(false)}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent
          id="share-view-modal-description"
          className={classes.modalBody}
        >
          <GridContainer alignItems={"center"}>
            <GridItem xs={true} style={{ paddingRight: "0 !important" }}>
              {shareLoading ? (
                <GridContainer
                  alignItems={"center"}
                  justifyContent={"center"}
                  style={{ padding: "20px 0" }}
                >
                  <ThreeDots
                    visible={true}
                    color={clientColor}
                    ariaLabel="loading"
                    height={"12px"}
                  />
                </GridContainer>
              ) : (
                <CustomInput
                  labelText={t("results.scenarios.url_to_share")}
                  id="scenario_url"
                  value={shareURL}
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    type: "text",
                    autoComplete: "off",
                    readOnly: true,
                    onClick: () => handleCopy(),
                  }}
                  integration
                />
              )}
            </GridItem>
            {!shareLoading && (
              <GridItem>
                <Button
                  onClick={() => handleCopy()}
                  color="info"
                  simple
                  style={{ paddingLeft: "15px", paddingRight: "15px" }}
                >
                  {t("results.scenarios.copy")}
                </Button>
              </GridItem>
            )}
          </GridContainer>
          <div className={classes.modalFootnote}>
            <ul>
              <li>{t("results.scenarios.share_footnote1")}</li>
              <li>{t("results.scenarios.share_footnote2")}</li>
            </ul>
          </div>
        </DialogContent>
        <DialogActions className={classes.modalFooter}>
          <GridContainer>
            <GridItem></GridItem>
            <GridItem xs={true}></GridItem>
            <GridItem>
              <Button
                onClick={() => setShareModalOpen(false)}
                color="danger"
                simple
              >
                {t("results.filter_modal.close")}
              </Button>
            </GridItem>
          </GridContainer>
        </DialogActions>
      </Dialog>
    </>
  );
}

ScenarioControls.propTypes = {
  scenarioHook: PropTypes.shape({
    busy: PropTypes.bool,
    getDropdownOptions: PropTypes.func,
    handleSelection: PropTypes.func,
    nameModalOpen: PropTypes.bool,
    setNameModalOpen: PropTypes.func,
    nameModalInput: PropTypes.string,
    handleNameChange: PropTypes.func,
    handleNameApply: PropTypes.func,
    nameInputRef: PropTypes.object,
    shareModalOpen: PropTypes.bool,
    setShareModalOpen: PropTypes.func,
    shareURL: PropTypes.string,
    shareLoading: PropTypes.bool,
    copyShareURL: PropTypes.func,
  }).isRequired,
  disabled: PropTypes.bool,
  classes: PropTypes.shape({
    modalRoot: PropTypes.string,
    modal: PropTypes.string,
    modalBody: PropTypes.string,
    modalFooter: PropTypes.string,
    modalFootnote: PropTypes.string,
  }).isRequired,
  notifyCopy: PropTypes.func,
};
